import React, { useEffect, useMemo, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap'

const LoanSummaryModal = ({ show, onClose, summary }) => {
    const [activeTab, setActiveTab] = useState('flat')
    const summaryData = useMemo(() => summary || {}, [summary])

    useEffect(() => {
        if (show) {
            setActiveTab('flat')
        }
    }, [show])

    const flatRateSummary = useMemo(() => {
        const principal = Number(summaryData.loanAmount) || 0
        const annualRate = (Number(summaryData.interestRate) || 0) / 100
        const tenureMonths = Number(summaryData.tenureMonths) || 0
        const tenureYears = tenureMonths / 12
        const totalInterest = principal * annualRate * tenureYears
        const totalPayable = principal + totalInterest
        const monthlyEmi = tenureMonths > 0 ? totalPayable / tenureMonths : 0

        return {
            ...summaryData,
            monthlyEmi: Number((monthlyEmi || 0).toFixed(2)),
            totalPayable: Number((totalPayable || 0).toFixed(2))
        }
    }, [summaryData])

    const activeSummary = activeTab === 'flat' ? flatRateSummary : summaryData

    const formatCurrency = (amount) => {
        const value = Number(amount) || 0
        try {
            return value.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
        } catch (_) {
            return `₹${value.toFixed(2)}`
        }
    }

    const handleClose = () => {
        if (typeof onClose === 'function') {
            onClose(activeSummary)
        }
    }

    if (!summary) {
        return null
    }

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static">
            <ModalHeader closeButton>
                <ModalTitle>Loan Summary</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <ul className="nav nav-tabs mb-3" role="tablist">
                    <li className="nav-item" role="presentation">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'flat' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('flat')}
                        >
                            Flat Rate
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'reducing' ? 'active' : ''}`}
                            role="tab"
                            onClick={() => setActiveTab('reducing')}
                        >
                            Reducing Rate
                        </button>
                    </li>
                </ul>
                <div className="table-responsive">
                    <table className="table table-sm mb-0">
                        <tbody>
                            <tr>
                                <td>Vehicle Cost</td>
                                <td className="text-end">{formatCurrency(summaryData.vehicleCost)}</td>
                            </tr>
                            <tr>
                                <td>Down Payment</td>
                                <td className="text-end">{formatCurrency(summaryData.downPayment)}</td>
                            </tr>
                            <tr>
                                <td>Processing Fees</td>
                                <td className="text-end">{formatCurrency(summaryData.processingFees)}</td>
                            </tr>
                            <tr>
                                <td>Loan Amount (incl. fees)</td>
                                <td className="text-end">{formatCurrency(summaryData.loanAmount)}</td>
                            </tr>
                            <tr>
                                <td>Interest Rate (annual)</td>
                                <td className="text-end">{summaryData.interestRate || 0}%</td>
                            </tr>
                            <tr>
                                <td>Tenure (months)</td>
                                <td className="text-end">{summaryData.tenureMonths}</td>
                            </tr>
                            <tr>
                                <td>EMI / month</td>
                                <td className="text-end fw-semibold">{formatCurrency(activeSummary.monthlyEmi)}</td>
                            </tr>
                            <tr>
                                <td>Total Payable</td>
                                <td className="text-end">{formatCurrency(activeSummary.totalPayable)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn btn-primary" onClick={handleClose}>
                    Close
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default LoanSummaryModal
